// src/components/article/ArticleComponents.jsx
// All self-contained article UI components — React + Vite, no dependencies
// except react-router-dom for <Link>

import { useState } from 'react';
import { Link } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────
// BiasBadge
// ─────────────────────────────────────────────────────────────────────────

export function BiasBadge({ bias }) {
    const map = {
        BULLISH: { cls: 'ffa-badge--bull', dot: 'ffa-dot--bull', label: 'BULLISH' },
        BEARISH: { cls: 'ffa-badge--bear', dot: 'ffa-dot--bear', label: 'BEARISH' },
        NEUTRAL: { cls: 'ffa-badge--neutral', dot: 'ffa-dot--neutral', label: 'NEUTRAL' },
    };
    const cfg = map[bias?.toUpperCase()] || map.NEUTRAL;

    return (
        <span className={`ffa-badge ${cfg.cls}`}>
            <span className={`ffa-dot ${cfg.dot}`}>
                <span className="ffa-dot-ping" />
                <span className="ffa-dot-core" />
            </span>
            {cfg.label}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// ReadingProgressBar
// ─────────────────────────────────────────────────────────────────────────

export function ReadingProgressBar({ progress }) {
    const opacity = progress >= 99 ? 0 : 1;
    return (
        <div
            className="ffa-progress-track"
            style={{ opacity, transition: 'opacity 0.6s' }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
        >
            <div
                className="ffa-progress-fill"
                style={{
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, #D4A843 ${Math.max(0, progress - 40)}%, #00C896 100%)`,
                }}
            />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// TOCSidebar
// ─────────────────────────────────────────────────────────────────────────

export function TOCSidebar({ toc = [], activeId }) {
    if (!toc.length) return null;

    const handleClick = (e, id) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <nav className="ffa-toc" aria-label="Table of contents">
            <p className="ffa-toc__label">Contents</p>
            <ul className="ffa-toc__list">
                {toc.map((item) => (
                    <li key={item.id} className={`ffa-toc__item ${item.level === 3 ? 'ffa-toc__item--h3' : ''}`}>
                        <a
                            href={`#${item.id}`}
                            onClick={(e) => handleClick(e, item.id)}
                            className={`ffa-toc__link ${activeId === item.id ? 'ffa-toc__link--active' : ''}`}
                        >
                            {item.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// ScenarioMatrix
// ─────────────────────────────────────────────────────────────────────────

export function ScenarioMatrix({ scenarios = [] }) {
    if (!scenarios.length) return null;

    const colours = {
        Bull: 'ffa-scenario--bull',
        Bear: 'ffa-scenario--bear',
        Sideways: 'ffa-scenario--sideways',
    };

    return (
        <div className="ffa-scenario-wrap">
            <h3 className="ffa-scenario-title">📊 Scenario Matrix</h3>
            <div className="ffa-scenario-grid">
                {scenarios.map((s) => (
                    <div key={s.scenario} className={`ffa-scenario-card ${colours[s.scenario] || 'ffa-scenario--sideways'}`}>
                        <div className="ffa-scenario-header">
                            <span className="ffa-scenario-name">{s.scenario}</span>
                            <span className="ffa-scenario-prob">{s.probability}%</span>
                        </div>
                        <div className="ffa-prob-bar">
                            <div className="ffa-prob-fill" style={{ width: `${s.probability}%` }} />
                        </div>
                        <p className="ffa-scenario-trigger">
                            <strong>Trigger:</strong> {s.trigger}
                        </p>
                        <p className="ffa-scenario-target">Target: {s.target}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// KeyLevelsBox
// ─────────────────────────────────────────────────────────────────────────

export function KeyLevelsBox({ levels }) {
    if (!levels?.resistance) return null;

    const [copied, setCopied] = useState('');

    const copy = (label, val) => {
        navigator.clipboard?.writeText(val).then(() => {
            setCopied(label);
            setTimeout(() => setCopied(''), 1500);
        });
    };

    const rows = [
        { label: 'Resistance', value: levels.resistance, cls: 'ffa-level--res' },
        { label: 'Support', value: levels.support, cls: 'ffa-level--sup' },
        { label: 'Invalidation', value: levels.invalidation, cls: 'ffa-level--inv' },
        { label: 'DXY Pivot', value: levels.dxy_pivot, cls: 'ffa-level--dxy' },
    ];

    return (
        <div className="ffa-levels-box">
            <p className="ffa-levels-title">🔑 Key Levels — XAU/USD</p>
            {rows.map((r) => (
                <div key={r.label} className="ffa-level-row">
                    <span className="ffa-level-label">{r.label}</span>
                    <div className="ffa-level-right">
                        <span className={`ffa-level-value ${r.cls}`}>{r.value}</span>
                        <button
                            className="ffa-level-copy"
                            onClick={() => copy(r.label, r.value)}
                            title="Copy level"
                        >
                            {copied === r.label ? '✓' : '⧉'}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// AuthorBio
// ─────────────────────────────────────────────────────────────────────────

export function AuthorBio({ author }) {
    if (!author?.name) return null;
    return (
        <div className="ffa-author">
            <img
                src={author.avatar_url || '/assets/og-image.png'}
                alt={author.name}
                className="ffa-author__avatar"
                loading="lazy"
            />
            <div className="ffa-author__body">
                <p className="ffa-author__name">{author.name}</p>
                <p className="ffa-author__title">{author.title}</p>
                <p className="ffa-author__creds">{author.credentials}</p>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// FAQAccordion
// ─────────────────────────────────────────────────────────────────────────

export function FAQAccordion({ faqs = [] }) {
    const [open, setOpen] = useState(null);
    if (!faqs.length) return null;

    return (
        <div className="ffa-faq">
            <h2 className="ffa-faq__title">Frequently Asked Questions</h2>
            {faqs.map((faq, i) => (
                <div key={i} className={`ffa-faq__item ${open === i ? 'ffa-faq__item--open' : ''}`}>
                    <button
                        className="ffa-faq__q"
                        onClick={() => setOpen(open === i ? null : i)}
                        aria-expanded={open === i}
                    >
                        <span>{faq.question}</span>
                        <span className={`ffa-faq__chevron ${open === i ? 'ffa-faq__chevron--open' : ''}`}>▼</span>
                    </button>
                    <div className="ffa-faq__answer-wrap" style={{ maxHeight: open === i ? '600px' : '0' }}>
                        <p className="ffa-faq__answer">{faq.answer}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// SignalCTABanner — mid-article live signal widget
// ─────────────────────────────────────────────────────────────────────────

export function SignalCTABanner({ bias = 'NEUTRAL', confidence = 0.90 }) {
    const confPct = Math.round((confidence || 0.9) * 100);
    const isLive = bias && bias !== 'NEUTRAL';

    return (
        <div className="ffa-signal-banner">
            <div className="ffa-signal-banner__left">
                <div className="ffa-signal-banner__live-row">
                    <span className="ffa-signal-live-dot">
                        <span className="ffa-signal-live-ping" />
                        <span className="ffa-signal-live-core" />
                    </span>
                    <span className="ffa-signal-banner__live-label">
                        {isLive ? 'Live Signal Active' : 'Signal Monitor Active'}
                    </span>
                </div>
                <p className="ffa-signal-banner__headline">
                    XAU/USD <BiasBadge bias={bias} /> — <span className="ffa-signal-conf">{confPct}% AI confidence</span>
                </p>
                <p className="ffa-signal-banner__sub">
                    3-layer validation confirmed · Entry · SL · TP provided
                </p>
            </div>
            <a href="https://www.forexfundai.com/features/elite-signals" className="ffa-signal-banner__cta">
                View Full Signal →
            </a>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// SidebarMacroBiasCard
// ─────────────────────────────────────────────────────────────────────────

export function SidebarMacroBiasCard({ bias, confidence }) {
    return (
        <div className="ffa-sidebar-macro">
            <p className="ffa-sidebar-macro__label">Macro Bias</p>
            <BiasBadge bias={bias} />
            <p className="ffa-sidebar-macro__conf">
                AI Confidence: {Math.round((confidence || 0.9) * 100)}%
            </p>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// SidebarCTA
// ─────────────────────────────────────────────────────────────────────────

export function SidebarCTA() {
    return (
        <div className="ffa-sidebar-cta">
            <p className="ffa-sidebar-cta__eyebrow">Live XAU/USD Signals</p>
            <p className="ffa-sidebar-cta__desc">
                Entry · SL · TP with 90%+ AI confidence gate. MT5 auto-execution included.
            </p>
            <a href="https://forexfundai.com/pricing" className="ffa-sidebar-cta__btn">
                Plans from $30/mo →
            </a>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// RelatedPosts
// ─────────────────────────────────────────────────────────────────────────

export function RelatedPosts({ posts = [] }) {
    if (!posts.length) return null;
    return (
        <div className="ffa-related">
            <h3 className="ffa-related__title">Related Research</h3>
            <div className="ffa-related__grid">
                {posts.map((p) => (
                    <Link key={p.slug} to={`/insights/${p.slug}`} className="ffa-related__card">
                        <div className="ffa-related__thumb">
                            <img
                                src={p.cover_image_url || '/assets/og-image.png'}
                                alt={p.title}
                                loading="lazy"
                            />
                        </div>
                        <div className="ffa-related__body">
                            <span className="ffa-related__cat">{p.category}</span>
                            <p className="ffa-related__title-text">{p.title}</p>
                            {p.reading_time && (
                                <span className="ffa-related__time">{p.reading_time} min read</span>
                            )}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// ArticleMeta — header meta row (archetype, bias, read time, word count)
// ─────────────────────────────────────────────────────────────────────────

export function ArticleMeta({ article }) {
    const archetypeNames = {
        A: 'Deep-Dive', B: 'Case Study', C: 'Debate',
        D: 'Risk Guide', E: 'Workflow', F: 'Forecast', G: 'Explainer',
    };

    return (
        <div className="ffa-article-meta">
            {article.archetype && (
                <span className="ffa-meta-chip ffa-meta-chip--archetype">
                    {archetypeNames[article.archetype] || `Type ${article.archetype}`}
                </span>
            )}
            <BiasBadge bias={article.macro_bias} />
            {article.reading_time && (
                <span className="ffa-meta-chip">{article.reading_time} min read</span>
            )}
            {article.word_count > 0 && (
                <span className="ffa-meta-chip">{article.word_count.toLocaleString()} words</span>
            )}
            {article.published_at && (
                <span className="ffa-meta-chip">
                    {new Date(article.published_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric',
                    })}
                </span>
            )}
        </div>
    );
}
